import React, { useMemo, useState } from "react";
import { PLANS, PLANS_META, USAGE_TYPES } from "@routebot/constants";
import MySkeleton from "components/ui/feedback/Skeleton";
import { useStatsFetch } from "./useStatsApi";
import {
  StatCard,
  SectionCard,
  RefreshButton,
  ErrorMessage,
  SimpleTable,
  RateBar,
  Badge,
  formatNumber,
} from "./StatComponents";

const USAGE_TYPE_META = [
  { key: USAGE_TYPES.MAPS__DIRECTIONS_SINGLE, label: "Tek yön", short: "Yön" },
  { key: USAGE_TYPES.MAPS__DIRECTIONS_SINGLE_WITH_TRAFFIC, label: "Trafikli yön", short: "Trafik" },
  { key: USAGE_TYPES.MAPS__DIRECTIONS_WITH_WAYPOINTS, label: "Ara noktalı yön", short: "Ara nokta" },
  { key: USAGE_TYPES.MAPS__ROUTE_OPTIMIZATION, label: "Rota optimizasyonu", short: "Optimizasyon" },
  { key: USAGE_TYPES.MAPS__ADDRESS_TO_NEAREST_ROAD_LOCATION, label: "Adres → konum", short: "Adres→konum" },
  { key: USAGE_TYPES.MAPS__LOCATION_TO_NEAREST_ROAD_ADDRESS, label: "Konum → adres", short: "Konum→adres" },
];

const TYPE_LABEL = Object.fromEntries(USAGE_TYPE_META.map((item) => [item.key, item.label]));

const PERIODS = [
  { key: "last3", label: "Son 3 gün" },
  { key: "last7", label: "Son 7 gün" },
  { key: "last30", label: "Son 30 gün" },
];

const emptyPeriods = () => ({ last3: 0, last7: 0, last30: 0 });

const periodCountsFor = (row, typeFilter) => {
  if (!typeFilter) return { last3: row.last3 || 0, last7: row.last7 || 0, last30: row.last30 || 0 };
  return row.byType?.[typeFilter] || emptyPeriods();
};

const planName = (plan) => PLANS_META[plan]?.name || plan || "-";

const formatQuota = (quota) => {
  if (quota === undefined || quota === null) return "—";
  if (!Number.isFinite(Number(quota))) return "sınırsız";
  return formatNumber(quota);
};

const SortHeader = ({ label, sortKey, currentKey, dir, onSort, align = "right" }) => {
  const active = currentKey === sortKey;
  return (
    <th
      onClick={() => onSort(sortKey)}
      style={{
        padding: "10px",
        textAlign: align,
        borderBottom: "2px solid #ddd",
        whiteSpace: "nowrap",
        cursor: "pointer",
        userSelect: "none",
        color: active ? "#1976d2" : "#333",
      }}
    >
      {label}
      {active ? (dir === "desc" ? " ↓" : " ↑") : ""}
    </th>
  );
};

const QuotaUsageSection = () => {
  const { data, isLoading, error, load } = useStatsFetch("/admin/statistics/quota-usage");
  const [typeFilter, setTypeFilter] = useState("");
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState("last30");
  const [sortDir, setSortDir] = useState("desc");
  const [expandedId, setExpandedId] = useState(null);

  const totals = useMemo(() => periodCountsFor(data?.totals || emptyPeriods(), typeFilter), [data, typeFilter]);

  const typeTotals = useMemo(() => {
    const byType = data?.totals?.byType || {};
    return USAGE_TYPE_META.map((item) => ({
      key: item.key,
      label: item.label,
      ...(byType[item.key] || emptyPeriods()),
    })).filter((row) => row.last30 > 0 || row.last7 > 0 || row.last3 > 0);
  }, [data]);

  const rows = useMemo(() => {
    const list = (data?.companies || [])
      .map((company) => ({ ...company, counts: periodCountsFor(company, typeFilter) }))
      .filter((company) => {
        if (typeFilter && company.counts.last30 === 0 && company.counts.last7 === 0 && company.counts.last3 === 0) {
          return false;
        }
        if (!search.trim()) return true;
        return String(company.company || "")
          .toLowerCase()
          .includes(search.trim().toLowerCase());
      });

    const factor = sortDir === "asc" ? 1 : -1;
    list.sort((a, b) => {
      if (sortKey === "company") {
        return factor * String(a.company).localeCompare(String(b.company), "tr");
      }
      if (sortKey === "plan") {
        return factor * String(a.plan || "").localeCompare(String(b.plan || ""), "tr");
      }
      return factor * ((a.counts[sortKey] || 0) - (b.counts[sortKey] || 0));
    });
    return list;
  }, [data, typeFilter, search, sortKey, sortDir]);

  const onSort = (key) => {
    if (sortKey === key) {
      setSortDir((prev) => (prev === "desc" ? "asc" : "desc"));
      return;
    }
    setSortKey(key);
    setSortDir(key === "company" || key === "plan" ? "asc" : "desc");
  };

  const typeBreakdownColumns = [
    { key: "label", label: "İşlem" },
    ...PERIODS.map((period) => ({
      key: period.key,
      label: period.label,
      align: "right",
      render: (row) => formatNumber(row[period.key]),
    })),
  ];

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "10px",
          marginBottom: "15px",
        }}
      >
        <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
          <button
            onClick={() => setTypeFilter("")}
            style={filterButtonStyle(!typeFilter)}
          >
            Tümü
          </button>
          {USAGE_TYPE_META.map((item) => (
            <button
              key={item.key}
              title={item.label}
              onClick={() => setTypeFilter(item.key)}
              style={filterButtonStyle(typeFilter === item.key)}
            >
              {item.short}
            </button>
          ))}
        </div>
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Şirket ara..."
            style={{
              padding: "6px 10px",
              border: "1px solid #ccc",
              borderRadius: "4px",
              fontSize: "13px",
              minWidth: "180px",
            }}
          />
          <RefreshButton onClick={() => load()} isLoading={isLoading} />
        </div>
      </div>

      <ErrorMessage message={error} />

      {isLoading && !data ? (
        <MySkeleton lineCount={10} />
      ) : data ? (
        <>
          <p style={{ color: "#666", fontSize: "13px", marginTop: 0, marginBottom: "16px" }}>
            Google Maps çağrıları; yalnızca kota sayılan (sağlayıcıya giden, faturalanan) kayıtlar. Önbellekten
            dönenler dahil değil. Kullanım kayıtları 90 gün sonra silinir. Kota limiti fatura dönemine göre
            uygulanır; bu tablo takvim pencereleridir (son 3 / 7 / 30 gün).
            {typeFilter ? ` Gösterilen işlem: ${TYPE_LABEL[typeFilter]}.` : ""}
          </p>

          <div style={{ display: "flex", flexWrap: "wrap", gap: "15px", marginBottom: "20px" }}>
            <StatCard label="Son 3 gün" value={totals.last3} color="#c62828" />
            <StatCard label="Son 7 gün" value={totals.last7} color="#ef6c00" />
            <StatCard label="Son 30 gün" value={totals.last30} color="#1976d2" />
            <StatCard
              label="Şirket"
              value={rows.length}
              sub={search || typeFilter ? `${formatNumber(data.companyCount)} toplam kullanım yapan` : "kullanım yapan"}
              color="#546e7a"
            />
          </div>

          {!typeFilter && typeTotals.length > 0 && (
            <SectionCard title="İşlem Tipi Toplamı">
              <SimpleTable
                columns={typeBreakdownColumns}
                rows={typeTotals}
                rowKey={(row) => row.key}
                maxHeight="280px"
                emptyText="Bu dönemde kota kullanımı yok."
              />
            </SectionCard>
          )}

          <SectionCard title={`Şirket Bazında Kullanım (${formatNumber(rows.length)})`}>
            {rows.length === 0 ? (
              <p style={{ color: "#999", margin: 0 }}>Bu filtrede kota kullanımı yok.</p>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
                  <thead>
                    <tr style={{ background: "#f5f5f5" }}>
                      <SortHeader
                        label="Şirket"
                        sortKey="company"
                        currentKey={sortKey}
                        dir={sortDir}
                        onSort={onSort}
                        align="left"
                      />
                      <SortHeader
                        label="Plan"
                        sortKey="plan"
                        currentKey={sortKey}
                        dir={sortDir}
                        onSort={onSort}
                        align="left"
                      />
                      {PERIODS.map((period) => (
                        <SortHeader
                          key={period.key}
                          label={period.label}
                          sortKey={period.key}
                          currentKey={sortKey}
                          dir={sortDir}
                          onSort={onSort}
                        />
                      ))}
                      {typeFilter && (
                        <th
                          style={{
                            padding: "10px",
                            textAlign: "left",
                            borderBottom: "2px solid #ddd",
                            minWidth: "160px",
                          }}
                        >
                          Kota (30 gün / plan)
                        </th>
                      )}
                      <th style={{ padding: "10px", textAlign: "center", borderBottom: "2px solid #ddd" }}>
                        Detay
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => {
                      const isExpanded = expandedId === row.companyId;
                      const quota = typeFilter ? row.quotas?.[typeFilter] : null;
                      const used30 = row.counts.last30;
                      const unlimited = row.plan === PLANS.UNLIMITED;
                      const typeRows = Object.entries(row.byType || {})
                        .map(([key, counts]) => ({ key, label: TYPE_LABEL[key] || key, ...counts }))
                        .filter((item) => item.last30 > 0 || item.last7 > 0 || item.last3 > 0);

                      return (
                        <React.Fragment key={row.companyId}>
                          <tr style={{ background: isExpanded ? "#e3f2fd" : "transparent" }}>
                            <td style={tdStyle}>
                              <div style={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
                                <span style={{ fontWeight: 500 }}>{row.company}</span>
                                {row.isDemo && (
                                  <Badge color="#fff3e0" textColor="#e65100">
                                    Demo
                                  </Badge>
                                )}
                                {row.isActive === false && (
                                  <Badge color="#eeeeee" textColor="#616161">
                                    Pasif
                                  </Badge>
                                )}
                              </div>
                            </td>
                            <td style={tdStyle}>{planName(row.plan)}</td>
                            {PERIODS.map((period) => (
                              <td key={period.key} style={{ ...tdStyle, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                                {formatNumber(row.counts[period.key])}
                              </td>
                            ))}
                            {typeFilter && (
                              <td style={tdStyle}>
                                {unlimited ? (
                                  <span style={{ fontSize: "13px", color: "#666" }}>
                                    {formatNumber(used30)} / sınırsız
                                  </span>
                                ) : quota ? (
                                  <RateBar rate={quota > 0 ? (used30 / quota) * 100 : 0} />
                                ) : (
                                  <span style={{ fontSize: "13px", color: "#999" }}>
                                    {formatNumber(used30)} / {formatQuota(quota)}
                                  </span>
                                )}
                                {!unlimited && quota ? (
                                  <div style={{ fontSize: "11px", color: "#888", marginTop: "2px" }}>
                                    {formatNumber(used30)} / {formatQuota(quota)}
                                  </div>
                                ) : null}
                              </td>
                            )}
                            <td style={{ ...tdStyle, textAlign: "center" }}>
                              <button
                                onClick={() => setExpandedId(isExpanded ? null : row.companyId)}
                                style={{
                                  background: isExpanded ? "#1976d2" : "none",
                                  color: isExpanded ? "#fff" : "#333",
                                  border: "1px solid #ccc",
                                  padding: "4px 8px",
                                  borderRadius: "4px",
                                  cursor: "pointer",
                                  fontSize: "12px",
                                }}
                              >
                                {isExpanded ? "Kapat" : "Tipler"}
                              </button>
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr style={{ background: "#e3f2fd" }}>
                              <td colSpan={typeFilter ? 7 : 6} style={{ padding: "12px 15px", borderBottom: "1px solid #eee" }}>
                                <div style={{ background: "#fff", padding: "12px", borderRadius: "6px" }}>
                                  <h4 style={{ margin: "0 0 8px 0", fontSize: "14px" }}>
                                    {row.company} — işlem kırılımı
                                  </h4>
                                  <SimpleTable
                                    columns={typeBreakdownColumns}
                                    rows={typeRows}
                                    rowKey={(item) => item.key}
                                    emptyText="Bu şirketin işlem kırılımı yok."
                                    maxHeight="280px"
                                  />
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </SectionCard>
        </>
      ) : null}
    </div>
  );
};

const tdStyle = {
  padding: "8px 10px",
  borderBottom: "1px solid #eee",
  verticalAlign: "middle",
};

const filterButtonStyle = (active) => ({
  padding: "6px 12px",
  borderRadius: "4px",
  border: active ? "1px solid #1976d2" : "1px solid #ccc",
  background: active ? "#1976d2" : "#fff",
  color: active ? "#fff" : "#333",
  cursor: "pointer",
  fontSize: "13px",
});

export default QuotaUsageSection;
